import { useEffect, useRef } from 'react';
import { Navigate, Outlet } from 'react-router';

import { showErrorNotification } from '../../../shared/utils/notifications';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Gates the Template Create route behind `canManageTemplates` — Standard users who navigate to
 * `/templates/new` directly are redirected back to the read-only Template Manager list. Editing
 * an existing template (`/templates/:id/edit`) stays reachable by Standard users — `TemplateEditorPage`
 * itself renders that route read-only for them instead of blocking access entirely. See
 * `usePermissions.ts` for how this will connect to real backend authorization later.
 */
export const TemplateCreateRouteGuard: React.FC = () => {
  const { canManageTemplates } = usePermissions();
  const hasShownErrorRef = useRef(false);

  useEffect(() => {
    if (!canManageTemplates && !hasShownErrorRef.current) {
      showErrorNotification(new Error('You do not have sufficient permissions to visit that route'));
      hasShownErrorRef.current = true;
    } else if (canManageTemplates) {
      hasShownErrorRef.current = false;
    }
  }, [canManageTemplates]);

  return canManageTemplates ? <Outlet /> : <Navigate replace to="/templates" />;
};
